import type { InviteTeamMembersModalProps } from "@v2/types/modalTypes";

import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { SelectionItem } from "@replo/design-system/blocks/selection/SelectionItem";
import { Button } from "@replo/design-system/components/button/Button";
import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { RadioGroup } from "@replo/design-system/components/radio-group/RadioGroup";
import { toast } from "@replo/design-system/components/toast/Toast";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import PillTextArea from "@v2/features/settings/shop/PillTextArea";
import { useIsWorkspaceOwner } from "@v2/hooks/useIsWorkspaceOwner";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { projectRouter, projectUtils } from "@v2/trpc/routes/project";
import { workspaceRouter, workspaceUtils } from "@v2/trpc/routes/workspace";
import {
  createFormErrorHandler,
  getFirstError,
} from "@v2/utils/form-error-handling";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

const inviteTargetSchema = z.enum([
  "workspace-member",
  "workspace-owner",
  "shop-member",
]);

const inviteFormSchema = z
  .object({
    emails: z
      .array(
        z.email({
          error: (issue) => {
            return typeof issue.input === "string"
              ? `"${issue.input}" is not a valid email address`
              : "Please enter a valid email address";
          },
        }),
      )
      .min(1, "Please enter at least one email address")
      .max(20, "You can invite up to 20 emails at a time.")
      .transform((emails) => emails.map((email) => email.trim().toLowerCase())),
    inviteTarget: inviteTargetSchema,
  })
  .superRefine(({ emails }, ctx) => {
    const seen = new Set<string>();
    const duplicates = new Set<string>();

    for (const email of emails) {
      const normalizedEmail = email.toLowerCase();
      if (seen.has(normalizedEmail)) {
        duplicates.add(email);
      } else {
        seen.add(normalizedEmail);
      }
    }

    if (duplicates.size > 0) {
      const duplicateList = Array.from(duplicates).join(", ");
      ctx.addIssue({
        path: ["emails"],
        code: "custom",
        message: `Email${duplicates.size > 1 ? "s" : ""} ${duplicateList} ${duplicates.size > 1 ? "are" : "is"} duplicated.`,
      });
    }
  });

export const InviteTeamMembersModal: React.FC<
  InviteTeamMembersModalProps & {
    open: boolean;
    onOpenChange: (open: boolean) => void;
  }
> = ({ workspaceId, email, open, onOpenChange }) => {
  const isMobile = useIsMobile();
  const logEvent = useLogAnalytics();

  const { project } = useCurrentProjectContext();
  const hasProject = Boolean(project?.id);
  const isWorkspaceOwner = useIsWorkspaceOwner(workspaceId);

  const { control, handleSubmit, formState, setError, watch } = useForm<
    z.infer<typeof inviteFormSchema>
  >({
    mode: "onChange",
    defaultValues: {
      emails: email ? [email] : [],
      inviteTarget: "workspace-member",
    },
    resolver: zodResolver(inviteFormSchema),
  });
  const errors = formState.errors;
  const firstError = getFirstError(errors);

  const emails = watch("emails");
  const inviteTarget = watch("inviteTarget");

  const {
    mutate: inviteWorkspaceMembers,
    isPending: isWorkspaceMembershipsLoading,
  } = workspaceRouter.inviteMembers.useMutation({
    onSuccess: () => {
      void workspaceUtils.getWorkspaceAndProjectMembers.invalidate(workspaceId);
      toast.success({
        title: "Invites sent",
        description: "The invites were sent successfully.",
      });
      logEvent("workspace.members.add", {
        numberOfInvites: emails.length,
        emails,
        permissionLevel:
          inviteTarget === "workspace-owner" ? "owner" : "member",
        workspaceId,
      });
      onOpenChange(false);
    },
    ...createFormErrorHandler(setError, {
      usersToGrantAccess: "emails",
    }),
  });

  const { mutate: inviteProjectMembers, isPending: isProjectInviting } =
    projectRouter.membership.inviteMembers.useMutation({
      onSuccess: (_data, variables) => {
        void projectUtils.membership.list.invalidate({
          projectId: variables.projectId,
        });
        void workspaceUtils.getWorkspaceAndProjectMembers.invalidate(
          workspaceId,
        );
        toast.success({
          title: "Invites sent",
          description: "The invites were sent successfully.",
        });
        onOpenChange(false);
      },
      ...createFormErrorHandler(setError),
    });

  const inviteOptions = [
    {
      value: "workspace-member",
      label: "Workspace Member",
      description:
        "Full access to create, edit, and publish. No billing control.",
    },
    ...(isWorkspaceOwner
      ? [
          {
            value: "workspace-owner",
            label: "Workspace Owner",
            description:
              "Full control including billing, permissions, and all projects.",
          },
        ]
      : []),
    ...(hasProject
      ? [
          {
            value: "shop-member",
            label: "Project Member",
            description:
              "Access to this project only. No workspace-level permissions.",
          },
        ]
      : []),
  ];

  const onSubmit = (data: z.infer<typeof inviteFormSchema>) => {
    const { emails, inviteTarget } = data;

    if (inviteTarget === "shop-member") {
      if (!project?.id) {
        return;
      }
      inviteProjectMembers({ projectId: project.id, emails });
    } else {
      if (!workspaceId) {
        return;
      }
      const role = inviteTarget === "workspace-owner" ? "owner" : "member";
      inviteWorkspaceMembers({
        workspaceId,
        usersToGrantAccess: emails.map((email) => ({ email, role })),
      });
    }
  };

  const footer = (
    <div className="flex justify-end w-full">
      <Tooltip content={firstError ?? ""} triggerAsChild>
        <Button
          variant="primary"
          size="base"
          render={<button type="submit" form="invite-form" />}
          loading={isWorkspaceMembershipsLoading || isProjectInviting}
          disabled={
            isWorkspaceMembershipsLoading ||
            isProjectInviting ||
            !formState.isValid
          }
        >
          Send Invites
        </Button>
      </Tooltip>
    </div>
  );

  const formContent = (
    <form
      id="invite-form"
      onSubmit={(e) => void handleSubmit(onSubmit)(e)}
      className="flex w-full flex-col gap-5"
    >
      <LabeledControl
        label="Invite members to this workspace"
        error={
          Array.isArray(errors.emails)
            ? errors.emails[0]?.message
            : errors.emails?.message
        }
      >
        <Controller
          name="emails"
          control={control}
          render={({ field }) => (
            <PillTextArea
              pills={field.value}
              onChange={(newPills) => {
                field.onChange(newPills);
              }}
            />
          )}
        />
      </LabeledControl>
      <LabeledControl label="Invite as" error={errors.inviteTarget?.message}>
        <Controller
          name="inviteTarget"
          control={control}
          render={({ field }) => (
            <RadioGroup
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value);
              }}
            >
              {inviteOptions.map((option) => (
                <SelectionItem
                  key={option.value}
                  type="radio"
                  id={option.value}
                  label={option.label}
                  description={option.description}
                  variant="outline"
                />
              ))}
            </RadioGroup>
          )}
        />
      </LabeledControl>
    </form>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title="Invite Team Members"
        footer={footer}
      >
        {formContent}
      </Drawer>
    );
  }

  return (
    <Modal
      title="Invite Team Members"
      open={open}
      onOpenChange={onOpenChange}
      size="base"
      footer={footer}
    >
      {formContent}
    </Modal>
  );
};
